import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthContext } from '../hooks/useAuthContext';
import { useTheme } from '../context/ThemeContext';
import { Dialog, DialogPanel } from '@headlessui/react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';

// Layout components
import Footer from '../components/layout/Footer';
import Container from '../components/common/Container';

// Employer page components
import EmployerValuePropositionSection from '../components/features/employer/EmployerValuePropositionSection';
import EmployerAdvantageSection from '../components/features/employer/EmployerAdvantageSection';
import EmployerCTAFormSection from '../components/features/employer/EmployerCTAFormSection';
import HeroSearchModal from '../components/features/employer/HeroSearchModal';

const navigation = [
  { name: 'Are you the top 1%? Join our network', href: '/', isMainLink: true },
];

const EmployerLandingPage = () => {
  const { user, authIsReady } = useAuthContext();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  
  // Hero search modal state
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Use useEffect for navigation - this must be at the top level, not after conditions
  useEffect(() => {
    if (authIsReady && user) {
      navigate('/network');
    }
  }, [authIsReady, user, navigate]);

  // Scroll to section functionality
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  // If auth is not ready yet, show loading indicator
  if (!authIsReady) {
    return (
      <div className={`min-h-screen flex items-center justify-center px-4 ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
        <div className="flex items-center">
          <div className="inline-block h-6 w-6 sm:h-8 sm:w-8 animate-spin rounded-full border-3 sm:border-4 border-solid border-purple-600 border-r-transparent"></div>
          <p className={`ml-3 text-sm sm:text-base ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
      {/* Header */}
      <header className="absolute inset-x-0 top-0 z-50">
        <nav aria-label="Global" className="flex items-center justify-between p-6 lg:px-8">
          <div className="flex lg:flex-1">
            <Link to="/" className="-m-1.5 p-1.5 flex items-center gap-2">
              <span className="sr-only">Burnt</span>
              <img src="/burnt-logo.svg" alt="Burnt Logo" className="h-6" />
              <span className={`text-2xl font-bold bg-gradient-to-r from-purple-500 to-purple-700 bg-clip-text text-transparent ${isDarkMode ? 'text-white' : ''}`}>burnt</span>
            </Link>
          </div>
          <div className="flex lg:hidden">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className={`-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
            >
              <span className="sr-only">Open main menu</span>
              <Bars3Icon aria-hidden="true" className="size-6" />
            </button>
          </div>
          <div className="hidden lg:flex lg:gap-x-12">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`text-sm/6 font-semibold hover:text-purple-600 transition-colors underline decoration-purple-600/30 hover:decoration-purple-600 underline-offset-4 ${
                  isDarkMode ? 'text-gray-100 hover:text-purple-400' : 'text-gray-900'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>
          <div className="hidden lg:flex lg:flex-1 lg:justify-end">
            <Link to="/" className={`text-sm/6 font-semibold hover:text-purple-600 transition-colors ${
              isDarkMode ? 'text-gray-100 hover:text-purple-400' : 'text-gray-900'
            }`}>
              Network
            </Link>
          </div>
        </nav>
        <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="lg:hidden">
          <div className="fixed inset-0 z-50" />
          <DialogPanel className={`fixed inset-y-0 right-0 z-50 w-full overflow-y-auto p-6 sm:max-w-sm sm:ring-1 ${
            isDarkMode 
              ? 'bg-gray-900 sm:ring-gray-700' 
              : 'bg-white sm:ring-gray-900/10'
          }`}>
            <div className="flex items-center justify-between">
              <Link to="/" className="-m-1.5 p-1.5 flex items-center gap-2">
                <span className="sr-only">Burnt</span>
                <img src="/burnt-logo.svg" alt="Burnt Logo" className="h-6" />
                <span className={`text-2xl font-bold bg-gradient-to-r from-purple-500 to-purple-700 bg-clip-text text-transparent ${isDarkMode ? 'text-white' : ''}`}>burnt</span>
              </Link>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className={`-m-2.5 rounded-md p-2.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
              >
                <span className="sr-only">Close menu</span>
                <XMarkIcon aria-hidden="true" className="size-6" />
              </button>
            </div>
            <div className="mt-6 flow-root">
              <div className={`-my-6 divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-500/10'}`}>
                <div className="space-y-2 py-6">
                  {navigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold hover:bg-gray-50 w-full text-left ${
                        isDarkMode 
                          ? 'text-gray-100 hover:bg-gray-800' 
                          : 'text-gray-900'
                      }`}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
                <div className="py-6">
                  <Link
                    to="/"
                    className={`-mx-3 block rounded-lg px-3 py-2.5 text-base/7 font-semibold hover:bg-gray-50 ${
                      isDarkMode 
                        ? 'text-gray-100 hover:bg-gray-800' 
                        : 'text-gray-900'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Network
                  </Link>
                </div>
              </div>
            </div>
          </DialogPanel>
        </Dialog>
      </header>

      {/* New Hero section - Mobile Optimized */}
      <div className={`relative overflow-hidden pt-20 pb-12 sm:pt-24 sm:pb-24 lg:pt-32 lg:pb-32 ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
        {/* Background decoration - Adjusted for mobile */}
        <div className="absolute inset-x-0 -top-8 sm:-top-16 -z-10 transform-gpu overflow-hidden blur-2xl sm:blur-3xl">
          <div
            className="relative left-1/2 -z-10 aspect-[1155/678] w-[28rem] sm:w-[36.125rem] max-w-none -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-purple-300/15 sm:from-purple-300/20 to-pink-300/15 sm:to-pink-300/20 opacity-30 sm:opacity-40 sm:left-[calc(50%-40rem)] lg:w-[72.1875rem]"
            style={{
              clipPath: 'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)'
            }}
          />
        </div>

        <Container className="relative z-10 px-6 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            {/* Main Title - Mobile-First Design */}
            <h1 className={`text-3xl xs:text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] xs:leading-[1.1] sm:leading-tight mb-8 sm:mb-10 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Find Your Next{' '}
              <span className="text-purple-600 dark:text-purple-400 block xs:block sm:inline">
                Top Performer
              </span>
            </h1>

            {/* Desktop Subtitle (before search bar) */}
            <div className="hidden sm:block">
              <p className={`text-xl leading-relaxed text-center max-w-3xl mx-auto mb-10 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                Access our exclusive network of elite professionals from finance, consulting, law, and tech. These high-achievers are ready to bring their expertise to your growing startup.
              </p>
            </div>

            {/* Search Bar Section - Mobile-First */}
            <div className="max-w-full sm:max-w-2xl mx-auto mb-8 sm:mb-10">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setIsModalOpen(true);
                }}
                className="relative"
              >
                {/* Mobile: Optimized Input */}
                <div className="block sm:hidden relative">
                  <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
                    <svg className={`h-6 w-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        setIsModalOpen(true);
                      }
                    }}
                    className={`block w-full rounded-2xl border-0 py-5 pl-14 pr-5 text-base shadow-lg ring-1 ring-inset transition-all duration-200 ${
                      isDarkMode 
                        ? 'bg-gray-800 text-white placeholder:text-gray-400 ring-gray-700 focus:ring-purple-500' 
                        : 'bg-white text-gray-900 placeholder:text-gray-500 ring-gray-300 focus:ring-purple-500'
                    } focus:ring-2`}
                    placeholder="e.g., Finance director in NYC"
                  />
                </div>

                {/* Desktop: Input with Integrated Button */}
                <div className="hidden sm:block relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <svg className={`h-5 w-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        setIsModalOpen(true);
                      }
                    }}
                    className={`block w-full rounded-2xl border-0 py-4 pl-12 pr-4 text-sm shadow-lg ring-1 ring-inset transition-all duration-200 ${
                      isDarkMode 
                        ? 'bg-gray-800 text-white placeholder:text-gray-400 ring-gray-700 focus:ring-purple-500' 
                        : 'bg-white text-gray-900 placeholder:text-gray-500 ring-gray-300 focus:ring-purple-500'
                    } focus:ring-2 sm:text-base`}
                    placeholder="Corporate development leader in NYC"
                  />
                </div>
              </form>
            </div>
              
            {/* Mobile Subtitle (after search bar) */}
            <div className="block sm:hidden mb-8">
              <p className={`text-base leading-relaxed text-center max-w-sm mx-auto ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                Access our exclusive network of elite professionals from finance, consulting, law, and tech.
              </p>
            </div>

            {/* Mobile-First Feature Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-6 lg:gap-8 mt-12 sm:mt-16 lg:mt-20">
              {/* Card 1: AI-Powered Matching */}
              <div className={`rounded-2xl p-6 sm:p-6 text-center transition-transform duration-200 active:scale-[0.98] ${
                isDarkMode ? 'bg-gray-800/50 ring-1 ring-gray-700' : 'bg-white/90 ring-1 ring-gray-200 shadow-lg'
              }`}>
                <div className="text-4xl sm:text-4xl mb-4 sm:mb-4">🎯</div>
                <h3 className={`text-lg sm:text-lg font-semibold mb-3 sm:mb-3 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  AI-Powered Matching
                </h3>
                <p className={`text-sm sm:text-sm leading-relaxed ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  Intelligent matching with elite candidates from our curated network
                </p>
              </div>

              {/* Card 2: Top 1% Talent */}
              <div className={`rounded-2xl p-6 sm:p-6 text-center transition-transform duration-200 active:scale-[0.98] ${
                isDarkMode ? 'bg-gray-800/50 ring-1 ring-gray-700' : 'bg-white/90 ring-1 ring-gray-200 shadow-lg'
              }`}>
                <div className="text-4xl sm:text-4xl mb-4 sm:mb-4">⭐</div>
                <h3 className={`text-lg sm:text-lg font-semibold mb-3 sm:mb-3 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  Top 1% Talent
                </h3>
                <p className={`text-sm sm:text-sm leading-relaxed ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  Proven high performers from finance, consulting, law, and tech
                </p>
              </div>

              {/* Card 3: Startup Ready */}
              <div className={`rounded-2xl p-6 sm:p-6 text-center transition-transform duration-200 active:scale-[0.98] sm:col-span-2 md:col-span-1 ${
                isDarkMode ? 'bg-gray-800/50 ring-1 ring-gray-700' : 'bg-white/90 ring-1 ring-gray-200 shadow-lg'
              }`}>
                <div className="text-4xl sm:text-4xl mb-4 sm:mb-4">🚀</div>
                <h3 className={`text-lg sm:text-lg font-semibold mb-3 sm:mb-3 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  Startup Ready
                </h3>
                <p className={`text-sm sm:text-sm leading-relaxed ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  Ready to leave corporate life for high-growth opportunities
                </p>
              </div>
            </div>

            {/* Mobile CTA Section */}
            <div className="mt-12 sm:mt-16">
              <div className="block sm:hidden">
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="w-full bg-purple-600 hover:bg-purple-700 active:bg-purple-800 text-white font-semibold py-5 px-8 rounded-2xl transition-all duration-200 shadow-xl hover:shadow-2xl active:scale-[0.98] text-lg"
                >
                  Find Top Talent Now
                </button>
                <p className={`mt-4 text-sm text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Free to start • No upfront costs
                </p>
              </div>
            </div>
          </div>
        </Container>
      </div>
      
      {/* Value proposition section with bento cards */}
      <EmployerValuePropositionSection isDarkMode={isDarkMode} />
      
      {/* Comparison with traditional hiring */}
      <EmployerAdvantageSection isDarkMode={isDarkMode} />
      
      {/* CTA Form Section */}
      <EmployerCTAFormSection 
        isDarkMode={isDarkMode} 
        initialSearchQuery={searchQuery}
      />
      
      <Footer isDarkMode={isDarkMode} />
      
      {/* Hero Search Modal - Mobile Optimized */}
      <HeroSearchModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialSearchQuery={searchQuery}
        isDarkMode={isDarkMode}
      />
    </div>
  );
};

export default EmployerLandingPage;
